import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone } from "lucide-react";
import { BRANDS, JOBS, getJob } from "../data/jobs";

/**
 * /work/:slug — full case-study detail page.
 * Hero image + story + color palette (brand + code + swatch) + gallery + CTAs.
 */
export default function WorkDetail() {
    const { slug } = useParams();
    const job = getJob(slug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!job) {
        return (
            <div
                data-testid="work-not-found"
                className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center"
            >
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                    404 · Wet paint
                </div>
                <h1 className="font-display uppercase text-5xl md:text-7xl mt-3 tracking-tight">
                    That job's not on
                    <br /> the wall.
                </h1>
                <Link
                    to="/"
                    data-testid="not-found-home-link"
                    className="mt-8 font-mono text-[11px] tracking-[0.3em] uppercase border border-white/40 hover:border-[#FFE600] hover:text-[#FFE600] px-6 py-3 transition-colors"
                >
                    ← Back to PaintKing
                </Link>
            </div>
        );
    }

    const currentIndex = JOBS.findIndex((j) => j.slug === job.slug);
    const nextJob = JOBS[(currentIndex + 1) % JOBS.length];

    return (
        <div
            data-testid="work-detail"
            className="min-h-screen bg-black text-white"
        >
            {/* Slim top nav */}
            <div className="sticky top-0 z-40 border-b border-white/10 backdrop-blur-lg bg-black/60">
                <div className="flex items-center justify-between px-4 md:px-8 py-3">
                    <Link
                        to="/"
                        data-testid="detail-back-link"
                        className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/80 hover:text-[#FFE600] transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        PaintKing · Home
                    </Link>
                    <a
                        href="tel:5616984044"
                        data-testid="detail-phone-cta"
                        className="glass-btn inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] tracking-[0.28em] uppercase hover:text-[#FFE600] transition-colors"
                    >
                        <Phone className="w-3.5 h-3.5" />
                        561 · 698 · 4044
                    </a>
                </div>
            </div>

            {/* Hero */}
            <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
                <motion.img
                    src={job.hero}
                    alt={job.title}
                    initial={{ scale: 1.08 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
                    >
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">
                            {job.service} · {job.location} · {job.year}
                        </div>
                        <h1 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight mt-3 max-w-5xl">
                            {job.title}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Story + Meta */}
            <section className="px-6 md:px-16 py-16 md:py-24">
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-8">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4">
                            [ The Job ]
                        </div>
                        <p className="font-display text-2xl md:text-4xl leading-[1.15] tracking-tight text-white/95 max-w-3xl">
                            {job.story}
                        </p>
                    </div>
                    <aside
                        data-testid="detail-meta"
                        className="col-span-12 md:col-span-4 glass p-6 md:p-8 self-start"
                    >
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4">
                            Job Highlights
                        </div>
                        <ul className="space-y-3">
                            {job.highlights.map((h) => (
                                <li
                                    key={h}
                                    className="flex items-start gap-3 font-mono text-[13px] text-white/85"
                                >
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFE600] flex-shrink-0" />
                                    {h}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4">
                            <div>
                                <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">
                                    Duration
                                </div>
                                <div className="font-display uppercase text-xl mt-1">
                                    {job.duration}
                                </div>
                            </div>
                            <div>
                                <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">
                                    Year
                                </div>
                                <div className="font-display uppercase text-xl mt-1">
                                    {job.year}
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* Palette */}
            {job.colors.length > 0 && (
                <section
                    data-testid="detail-palette"
                    className="px-6 md:px-16 py-12 md:py-20 border-t border-white/10"
                >
                    <div className="grid grid-cols-12 gap-6 mb-10">
                        <div className="col-span-12 md:col-span-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 pt-2">
                            [ Palette ]
                        </div>
                        <h2 className="col-span-12 md:col-span-10 font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                            The exact
                            <br />
                            <span className="rainbow-text">colors we used.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {job.colors.map((c) => {
                            const brand = BRANDS[c.brand] || { short: c.brand.slice(0, 3), accent: "#888" };
                            return (
                                <div
                                    key={c.code}
                                    data-testid={`color-chip-${c.code}`}
                                    className="glass border border-white/10 overflow-hidden group"
                                >
                                    <div
                                        className="h-40 md:h-52 w-full relative"
                                        style={{ background: c.hex }}
                                    >
                                        <div className="absolute top-3 left-3 flex items-center gap-2">
                                            <span
                                                className="font-display uppercase text-xs tracking-tight px-2 py-1 border"
                                                style={{
                                                    background: brand.accent,
                                                    color: "#fff",
                                                    borderColor: brand.accent,
                                                }}
                                            >
                                                {brand.short}
                                            </span>
                                            <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-black/70 bg-white/80 px-2 py-1">
                                                {c.use}
                                            </span>
                                        </div>
                                        <div className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.24em] uppercase bg-black/60 text-white px-2 py-1">
                                            {c.hex.toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50">
                                            {c.brand}
                                        </div>
                                        <div className="font-display uppercase text-2xl leading-tight tracking-tight mt-1">
                                            {c.name}
                                        </div>
                                        <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/70 mt-2">
                                            {c.code}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Gallery */}
            {job.gallery.length > 0 && (
                <section
                    data-testid="detail-gallery"
                    className="px-6 md:px-16 py-12 md:py-20 border-t border-white/10"
                >
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-6">
                        [ On-site frames ]
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {job.gallery.map((src, i) => (
                            <motion.img
                                key={src}
                                src={src}
                                alt={`${job.title} — frame ${i + 1}`}
                                loading="lazy"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.9,
                                    delay: i * 0.08,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className={`w-full object-cover ${
                                    i === 0 ? "aspect-[4/3]" : "aspect-[4/3]"
                                }`}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* CTA + Next */}
            <section className="px-6 md:px-16 py-16 md:py-24 border-t border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="glass p-8 md:p-10">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">
                            Want the same finish?
                        </div>
                        <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight mt-3">
                            Call Paul.
                            <br />
                            Free estimate.
                        </h3>
                        <a
                            href="tel:5616984044"
                            data-testid="detail-cta-phone"
                            className="mt-6 inline-flex items-center gap-3 border border-white/80 hover:border-[#FFE600] px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase group relative overflow-hidden"
                        >
                            <span className="relative z-10 group-hover:text-black transition-colors">
                                561 · 698 · 4044
                            </span>
                            <span className="absolute inset-0 bg-[#FFE600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                        </a>
                    </div>
                    <Link
                        to={`/work/${nextJob.slug}`}
                        data-testid="detail-next-job"
                        className="relative overflow-hidden border border-white/15 hover:border-white/40 transition-colors group block"
                    >
                        <img
                            src={nextJob.hero}
                            alt={nextJob.title}
                            className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-40 group-hover:opacity-70 transition-all duration-700"
                        />
                        <div className="relative p-8 md:p-10 min-h-[240px] flex flex-col justify-between">
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/70">
                                Next case study →
                            </div>
                            <div>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFE600]">
                                    {nextJob.service}
                                </div>
                                <div className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight mt-2">
                                    {nextJob.title}
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>
            </section>
        </div>
    );
}
